import { useNode, useEditor } from '@craftjs/core';
import { ROOT_NODE } from '@craftjs/utils';
import React, {
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  MutableRefObject,
} from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid'
import './RenderNode.scss';
import { Copy, Expand, Move, Trash } from 'lucide-react';

interface Props {
  render: ReactNode;
}

const RenderNode = ({ render }: Props) => {
  const { id } = useNode();
  const { actions, query, isActive } = useEditor((_, query) => ({
    isActive: query.getEvent('selected').contains(id),
  }));

  const {
    isHover,
    dom,
    name,
    moveable,
    deletable,
    connectors: { drag },
    parent,
  } = useNode((node) => ({
    isHover: node.events.hovered,
    dom: node.dom as HTMLElement,
    name: node.data.custom.displayName || node.data.displayName,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
    props: node.data.props,
  }));

  const currentRef = useRef<MutableRefObject<HTMLElement>>();

  useEffect(() => {
    if (dom) {
      if (isHover) {
        dom.classList.toggle('node--hovered');
      } else {
        dom.classList.remove('node--hovered');
      }
    }
  }, [dom, isHover]);

  useEffect(() => {
    if (dom) {
      if (isActive) {
        dom.classList.add('node--selected');
      } else {
        dom.classList.remove('node--selected');
      }
    }
  }, [dom, isActive]);

  const getPos = useCallback((dom: HTMLElement) => {
    const { top, left, bottom, height } = dom
      ? dom.getBoundingClientRect()
      : { top: 0, left: 0, bottom: 0, height: 0 };
    return {
      top: `${top > 0 ? top : bottom}px`,
      left: `${left}px`,
      height: `${height}px`,
    };
  }, []);

  const scroll = useCallback(() => {
    const { current: currentDOM } = currentRef;

    if (!currentDOM) return;
    const { top, left } = getPos(dom);
    currentDOM.style.top = top;
    currentDOM.style.left = left;
  }, [dom, getPos]);




  const getCloneTree = useCallback((idToClone: string) => {
    const tree = query.node(idToClone).toNodeTree();
    const newNodes = {};
 
    const changeNodeId = (node, newParentId) => {
      const newNodeId = nanoid();

      const childNodes = node.data.nodes.map((childId: any) =>
        changeNodeId(tree.nodes[childId], newNodeId)
      );

      const linkedNodes = Object.keys(node.data.linkedNodes).reduce(
        (accum, id) => {
          const linkedNodeId = changeNodeId(
            tree.nodes[node.data.linkedNodes[id]],
            newNodeId
          );
          return {
            ...accum,
            [id]: linkedNodeId
          };
        },
        {}
      );

      newNodes[newNodeId] = {
        ...node,
        id: newNodeId,
        data: {
          ...node.data,
          parent: newParentId || node.data.parent,
          nodes: childNodes,
          linkedNodes
        }
      };
      return newNodeId;
    };

    const rootNodeId = changeNodeId(tree.nodes[tree.rootNodeId], "");
    
    return {
      rootNodeId,
      nodes: newNodes
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleClone = (e, id) => {
    e.preventDefault();
    const theNode = query.node(id).get();
    const parentNode = query.node(theNode.data.parent).get();
    const indexToAdd = parentNode.data.nodes.indexOf(id);
    const tree = getCloneTree(id);
    actions.addNodeTree(tree, parentNode.id, indexToAdd + 1);
  };


  useEffect(() => {
    document
      .querySelector('.editor__canvas')
      ?.addEventListener('scroll', scroll);

    return () => {
      document
        .querySelector('.editor__canvas')
        ?.removeEventListener('scroll', scroll);
    };
  }, [scroll]);

  return (
    <>
      {isHover || isActive
        ? ReactDOM.createPortal(
            <div
              ref={currentRef as any}
              className="node__actions"
              style={{
                left: getPos(dom).left,
                top: getPos(dom).top,
              }}
            >
              <span className="node__actions__title">{name}</span>
              {moveable ? (
                <span className="node__actions__move" ref={drag as any}>
                  <button>
                    <Move size={16} color="white" />
                  </button>
                </span>
              ) : null}
              {id !== ROOT_NODE && (
                <button
                  className="node__actions__select-parent"
                  onClick={() => {
                    actions.selectNode(parent as any);
                  }}
                >
                  <Expand size={16} color="white" />
                </button>
              )}
              {id !== ROOT_NODE && (
                <button
                  onClick={(e) => {
                    handleClone(e, id);
                  }}
                >
                  <Copy size={16} color="white" />
                </button>
              )}
              {deletable ? (
                <button
                  className="node__actions__delete"
                  onMouseDown={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    actions.delete(id);
                  }}
                >
                  <Trash size={16} color="white" />
                </button>
              ) : null}
            </div>,
            document.querySelector('.editor__body') as any
          )
        : null}
      {render}
    </>
  );
};

export default RenderNode;
